import React, { useEffect, useMemo, useState } from "react";
import { Gauge, Plus, Trash2, Zap, ArrowDownToLine, ArrowUpFromLine, Pencil, X, GripVertical, Download, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useSafeBackdropClose } from "../../lib/modal";

const METER_TYPE_META = {
  generation: { icon: Zap,             color: "#059669", label: "Generation" },
  import:     { icon: ArrowDownToLine, color: "#0284c7", label: "Import"     },
  export:     { icon: ArrowUpFromLine, color: "#f97316", label: "Export"     },
};
const METER_TYPE_ORDER = ["generation", "import", "export"];

/**
 * MetersPanel — CRUD list of meters registered against a project. Some
 * sites have multiple PV installs (per unit + a communal canopy) each
 * with its own metering; this panel is where they live. Meters here also
 * populate the CSV-import dialog's per-meter dropdown.
 *
 * Layout: meters are grouped by type (Generation → Import → Export) with
 * a per-type subtotal chip. Rows within a group can be dragged to reorder
 * (persists via /projects/{pid}/meters/reorder). Data is aggregated by
 * meter_type server-side so extra meters accumulate automatically.
 */
export default function MetersPanel({ projectId }) {
  const [meters, setMeters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // Meter | "new" | null
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/meters`);
      setMeters(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [projectId]);

  const migrateLegacy = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/meters/migrate-legacy`);
      if (data.created > 0) toast.success(`Imported ${data.created} meter${data.created === 1 ? "" : "s"} from Edit Site config`);
      else toast.info("Nothing to import — meters already cover the Edit Site config");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Import failed"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete meter "${m.label}"? Any imports referencing it will keep their data but lose the meter link.`)) return;
    try {
      await api.delete(`/meters/${m.id}`);
      toast.success("Meter deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Group by meter_type. Server already sorts by (type, display_order) so
  // arrays are in the operator's chosen order.
  const grouped = useMemo(() => {
    const g = { generation: [], import: [], export: [] };
    for (const m of meters) {
      const t = METER_TYPE_ORDER.includes(m.meter_type) ? m.meter_type : "generation";
      g[t].push(m);
    }
    return g;
  }, [meters]);

  // Drag-and-drop reorder within a group. Reorders locally for instant
  // feedback, then persists the whole group's IDs to the backend. Reloads
  // on failure so state stays truthful.
  const reorderWithin = async (typeKey, fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const list = [...grouped[typeKey]];
    const fromIdx = list.findIndex(m => m.id === fromId);
    const toIdx = list.findIndex(m => m.id === toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    // Optimistic local update — merge back with the other groups in the
    // canonical Generation → Import → Export order.
    const merged = METER_TYPE_ORDER.flatMap(t => t === typeKey ? list : grouped[t]);
    setMeters(merged);
    try {
      await api.post(`/projects/${projectId}/meters/reorder`, {
        ordered_ids: list.map(m => m.id),
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reorder failed");
      await load();
    }
  };

  const onDragStart = (e, m) => {
    setDragId(m.id);
    e.dataTransfer.effectAllowed = "move";
    // Firefox needs some data.
    try { e.dataTransfer.setData("text/plain", m.id); } catch {}
  };
  const onDragOver = (e, m) => {
    // Only accept intra-group drops.
    if (!dragId) return;
    const dragged = meters.find(x => x.id === dragId);
    if (!dragged || dragged.meter_type !== m.meter_type) return;
    e.preventDefault();
    setDragOverId(m.id);
  };
  const onDrop = (e, m) => {
    e.preventDefault();
    if (dragId && dragOverId && dragId !== m.id) {
      reorderWithin(m.meter_type, dragId, m.id);
    }
    setDragId(null);
    setDragOverId(null);
  };
  const onDragEnd = () => { setDragId(null); setDragOverId(null); };

  const totalCount = meters.length;

  return (
    <section className="swiss-card p-6" data-testid="meters-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label flex items-center gap-2"><Gauge size={11} /> Meters · {totalCount}</div>
          <div className="font-display text-xl font-semibold mt-1">
            Physical meters on this project
          </div>
          <p className="muted text-xs mt-1">
            Grouped by Generation / Import / Export. Add as many as the site has — values within
            a group are accumulated in analytics. Drag the handle to reorder within a group.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={migrateLegacy} className="btn-ghost text-xs" data-testid="meters-migrate-btn"
                  title="Create meters from this site's Edit Site / MeterOnline config">
            <Download size={13} /> Import from Edit Site
          </button>
          <button onClick={() => setEditing("new")} className="btn-primary" data-testid="add-meter-btn">
            <Plus size={13} /> Add meter
          </button>
        </div>
      </div>

      {loading && <div className="muted text-xs font-mono uppercase tracking-widest">Loading meters…</div>}
      {!loading && !totalCount && (
        <div className="p-8 border border-dashed border-[var(--line)] text-center muted text-sm"
             data-testid="meters-empty-state">
          No meters registered yet. Add one for each generation, import or export flow the site has.
        </div>
      )}

      {!loading && totalCount > 0 && METER_TYPE_ORDER.map(typeKey => {
        const list = grouped[typeKey];
        const meta = METER_TYPE_META[typeKey];
        const Icon = meta.icon;
        return (
          <div key={typeKey} className="mb-5" data-testid={`meter-type-group-${typeKey}`}>
            <div className="flex items-center gap-2 mb-2">
              <Icon size={13} style={{ color: meta.color }} />
              <div
                className="text-[10px] font-mono uppercase tracking-widest font-semibold"
                style={{ color: meta.color }}
              >
                {meta.label}
              </div>
              <span
                className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                style={{ background: `${meta.color}15`, color: meta.color }}
                data-testid={`meter-type-count-${typeKey}`}
              >
                {list.length} meter{list.length === 1 ? "" : "s"} · accumulated
              </span>
            </div>
            {list.length === 0 ? (
              <div className="p-3 border border-dashed border-[var(--line)] text-[11px] muted text-center"
                   data-testid={`meter-type-empty-${typeKey}`}>
                No {meta.label.toLowerCase()} meters yet.
              </div>
            ) : (
              <div className="space-y-1">
                {list.map(m => (
                  <div
                    key={m.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, m)}
                    onDragOver={(e) => onDragOver(e, m)}
                    onDrop={(e) => onDrop(e, m)}
                    onDragEnd={onDragEnd}
                    className={`flex items-center gap-3 p-3 border bg-white transition-colors ${
                      dragOverId === m.id ? "border-[var(--accent-hover)] shadow-sm" : "border-[var(--line)] hover:border-[var(--accent)]"
                    } ${dragId === m.id ? "opacity-40" : ""}`}
                    data-testid={`meter-row-${m.id}`}
                  >
                    <button
                      type="button"
                      className="cursor-grab active:cursor-grabbing muted hover:text-[var(--ink)]"
                      title="Drag to reorder"
                      aria-label={`Drag ${m.label} to reorder`}
                      data-testid={`meter-drag-handle-${m.id}`}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <GripVertical size={14} />
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {m.label}
                        {m.install_group && (
                          <span className="ml-2 text-[10px] font-mono muted">{m.install_group}</span>
                        )}
                        {!m.active && <span className="ml-2 tag">inactive</span>}
                      </div>
                      <div className="text-[11px] muted font-mono">
                        {m.meter_number || "no number"}{m.source ? ` · ${m.source}` : ""}
                        {(m.meter_type === "import" || m.meter_type === "export") && (
                          <span className="ml-1" data-testid={`meter-direction-${m.id}`}>
                            · {m.direction === "reverse" ? "⟲ reverse" : m.direction === "forward" ? "→ forward" : "auto"}
                          </span>
                        )}
                        {(m.meteronline_username || m.solaredge_api_key || m.solis_key_id) && (
                          <span className="ml-1 text-[var(--accent-hover)]" title="Has own sync credentials">· 🔑 own login</span>
                        )}
                      </div>
                    </div>
                    <button onClick={() => remove(m)} className="btn-ghost !px-2.5 text-red-700 hover:text-red-800"
                            data-testid={`delete-meter-${m.id}`} title="Delete" aria-label="Delete meter">
                      <Trash2 size={13} />
                    </button>
                    <button onClick={() => setEditing(m)} className="btn-ghost !px-2.5" data-testid={`edit-meter-${m.id}`} title="Edit" aria-label="Edit meter">
                      <Pencil size={13} />
                    </button>
                    <Link to={`/analytics?site=${projectId}`} className="btn-ghost" data-testid={`open-meter-${m.id}`} title="Open this site's meter data in Reporting & analysis">
                      Open <ChevronRight size={13} />
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {editing && (
        <MeterFormModal
          projectId={projectId}
          meter={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}


function MeterFormModal({ projectId, meter, onClose, onSaved }) {
  const [form, setForm] = useState(meter || {
    meter_type: "generation", label: "", meter_number: "", direction: "",
    install_group: "", source: "", active: true, notes: "",
    meteronline_username: "", meteronline_password: "",
    solaredge_site_id: "", solaredge_api_key: "",
    solis_key_id: "", solis_key_secret: "", solis_inverter_id: "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isNew = !meter;
  const isFlow = form.meter_type === "import" || form.meter_type === "export";

  const save = async () => {
    if (!form.label.trim()) return toast.error("Label is required");
    // Only persist credentials for the currently-selected source so stale
    // creds from a previously-chosen source aren't saved silently.
    const payload = { ...form };
    if (form.source !== "meteronline") { payload.meteronline_username = ""; payload.meteronline_password = ""; }
    if (form.source !== "solaredge") { payload.solaredge_site_id = ""; payload.solaredge_api_key = ""; }
    if (form.source !== "solis") { payload.solis_key_id = ""; payload.solis_key_secret = ""; payload.solis_inverter_id = ""; }
    if (form.meter_type === "generation") payload.direction = "";
    setBusy(true);
    try {
      if (isNew) {
        await api.post(`/projects/${projectId}/meters`, payload);
        toast.success("Meter added");
      } else {
        await api.patch(`/meters/${meter.id}`, payload);
        toast.success("Meter saved");
      }
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" {...useSafeBackdropClose(onClose)} data-testid="meter-form-modal">
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="flex items-start justify-between p-5 border-b border-[var(--line)]">
          <div>
            <div className="label">{isNew ? "Add meter" : "Edit meter"}</div>
            <h2 className="font-display text-2xl font-bold mt-1">
              {isNew ? "Register a new meter" : form.label}
            </h2>
          </div>
          <button onClick={onClose} className="btn-ghost"><X size={14} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <label className="space-y-1">
              <div className="label">Type</div>
              <select className="input-box" value={form.meter_type} onChange={upd("meter_type")}
                      data-testid="meter-form-type">
                <option value="generation">Generation</option>
                <option value="import">Import</option>
                <option value="export">Export</option>
              </select>
            </label>
            <label className="space-y-1">
              <div className="label">Label *</div>
              <input className="input-swiss" value={form.label} onChange={upd("label")}
                     placeholder="e.g. Unit A Rooftop"
                     data-testid="meter-form-label" />
            </label>
            <label className="space-y-1">
              <div className="label">Install group</div>
              <input className="input-swiss" value={form.install_group || ""} onChange={upd("install_group")}
                     placeholder="Rooftop A · Communal · Canopy…"
                     data-testid="meter-form-group" />
            </label>
            <label className="space-y-1">
              <div className="label">Meter number / MPAN</div>
              <input className="input-swiss font-mono text-xs" value={form.meter_number || ""} onChange={upd("meter_number")}
                     placeholder="2000012345678"
                     data-testid="meter-form-number" />
            </label>
            <label className="space-y-1">
              <div className="label">Source (optional)</div>
              <select className="input-box" value={form.source || ""} onChange={upd("source")}
                      data-testid="meter-form-source">
                <option value="">— none / manual —</option>
                <option value="meteronline">MeterOnline</option>
                <option value="solaredge">SolarEdge</option>
                <option value="solis">Solis</option>
                <option value="manual">Manual (CSV only)</option>
              </select>
            </label>
            {isFlow && (
              <label className="space-y-1">
                <div className="label">Reading direction</div>
                <select className="input-box" value={form.direction || ""} onChange={upd("direction")}
                        data-testid="meter-form-direction">
                  <option value="">Auto (import→forward, export→reverse if same meter)</option>
                  <option value="forward">Forward register (consumption / dedicated export)</option>
                  <option value="reverse">Reverse register (export off a bi-directional meter)</option>
                </select>
              </label>
            )}
            <label className="space-y-1">
              <div className="label">Active</div>
              <select className="input-box" value={form.active ? "1" : "0"}
                      onChange={(e) => setForm({ ...form, active: e.target.value === "1" })}>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </label>
          </div>

          {/* Per-meter sync credentials — override the project's shared login.
              Shown contextually by source. */}
          {form.source === "meteronline" && (
            <div className="grid grid-cols-2 gap-4 p-3 rounded border border-[var(--line)] bg-[var(--surface-2)]"
                 data-testid="meter-form-creds-meteronline">
              <div className="col-span-2 label flex items-center gap-2"><Gauge size={11} /> MeterOnline credentials (optional — blank = use site default)</div>
              <label className="space-y-1">
                <div className="label">Username</div>
                <input className="input-swiss" value={form.meteronline_username || ""} onChange={upd("meteronline_username")}
                       data-testid="meter-form-mo-user" />
              </label>
              <label className="space-y-1">
                <div className="label">API password</div>
                <input type="password" className="input-swiss" value={form.meteronline_password || ""} onChange={upd("meteronline_password")}
                       data-testid="meter-form-mo-pass" />
              </label>
            </div>
          )}
          {form.source === "solaredge" && (
            <div className="grid grid-cols-2 gap-4 p-3 rounded border border-[var(--line)] bg-[var(--surface-2)]"
                 data-testid="meter-form-creds-solaredge">
              <div className="col-span-2 label">SolarEdge (optional — blank = use site default)</div>
              <label className="space-y-1">
                <div className="label">Site ID</div>
                <input className="input-swiss font-mono text-xs" value={form.solaredge_site_id || ""} onChange={upd("solaredge_site_id")} />
              </label>
              <label className="space-y-1">
                <div className="label">API key</div>
                <input type="password" className="input-swiss font-mono text-xs" value={form.solaredge_api_key || ""} onChange={upd("solaredge_api_key")} />
              </label>
            </div>
          )}
          {form.source === "solis" && (
            <div className="grid grid-cols-3 gap-4 p-3 rounded border border-[var(--line)] bg-[var(--surface-2)]"
                 data-testid="meter-form-creds-solis">
              <div className="col-span-3 label">Solis / SolisCloud (optional — blank = use site default)</div>
              <label className="space-y-1">
                <div className="label">Key ID</div>
                <input className="input-swiss font-mono text-xs" value={form.solis_key_id || ""} onChange={upd("solis_key_id")} />
              </label>
              <label className="space-y-1">
                <div className="label">Key secret</div>
                <input type="password" className="input-swiss font-mono text-xs" value={form.solis_key_secret || ""} onChange={upd("solis_key_secret")} />
              </label>
              <label className="space-y-1">
                <div className="label">Inverter ID</div>
                <input className="input-swiss font-mono text-xs" value={form.solis_inverter_id || ""} onChange={upd("solis_inverter_id")} />
              </label>
            </div>
          )}
          <label className="space-y-1 block">
            <div className="label">Notes</div>
            <textarea className="input-box" rows={2} value={form.notes || ""} onChange={upd("notes")} />
          </label>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-[var(--line)]">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-accent"
                  data-testid="meter-form-save">
            {busy ? "Saving…" : (isNew ? "Add meter" : "Save changes")}
          </button>
        </div>
      </div>
    </div>
  );
}
